import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';
import RankRing from './gamification/RankRing';
import { getRankName } from '../utils/iconMapper';
import { computeBadges, RARITY_STYLE, BadgeStats } from '../config/badges';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  progress: number;
  maxProgress: number;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

function generateAchievements(stats: BadgeStats): Achievement[] {
  const rows: [string, string, string, string, number, number][] = [
    ['first-challenge', 'First Steps', 'Complete your first challenge', 'Footprints', stats.challengesCompleted, 1],
    ['challenges-5', 'Getting Started', 'Complete 5 challenges', 'Sparkles', stats.challengesCompleted, 5],
    ['challenges-10', 'Dedicated Student', 'Complete 10 challenges', 'BookOpenCheck', stats.challengesCompleted, 10],
    ['challenges-25', 'Skilled Practitioner', 'Complete 25 challenges', 'Award', stats.challengesCompleted, 25],
    ['challenges-50', 'Master Practitioner', 'Complete 50 challenges', 'Crown', stats.challengesCompleted, 50],
    ['streak-3', 'Building Momentum', 'Maintain a 3-day training streak', 'Flame', stats.streak, 3],
    ['streak-7', 'Consistent Practice', 'Maintain a 7-day training streak', 'CalendarCheck', stats.streak, 7],
    ['streak-14', 'Steady Discipline', 'Maintain a 14-day training streak', 'Zap', stats.streak, 14],
    ['streak-30', 'Unwavering Focus', 'Maintain a 30-day training streak', 'Trophy', stats.streak, 30],
    ['points-500', 'Credit Collector', 'Earn 500 total credits', 'Coins', stats.points, 500],
    ['points-1000', 'Credit Hoarder', 'Earn 1,000 total credits', 'Gem', stats.points, 1000],
    ['points-2500', 'Treasurer', 'Earn 2,500 total credits', 'Landmark', stats.points, 2500],
    ['points-5000', 'Grand Collector', 'Earn 5,000 total credits', 'Gem', stats.points, 5000],
    ['level-2', 'Rising Star', 'Reach Level 2', 'TrendingUp', stats.level, 2],
    ['level-3', 'Skilled Apprentice', 'Reach Level 3', 'ArrowUpCircle', stats.level, 3],
    ['level-4', 'Elite Practitioner', 'Reach Level 4', 'Rocket', stats.level, 4],
    ['level-5', 'Legendary Master', 'Reach Level 5', 'Trophy', stats.level, 5],
    ['quest-1', 'Quest Beginner', 'Complete your first quest', 'Compass', stats.questsCompleted, 1],
    ['quest-3', 'Quest Adventurer', 'Complete 3 quests', 'Map', stats.questsCompleted, 3],
    ['quest-5', 'Quest Master', 'Complete all 5 quests', 'Crown', stats.questsCompleted, 5],
  ];
  return rows.map(([id, name, description, icon, value, max]) => ({
    id, name, description, icon,
    unlocked: value >= max,
    progress: Math.min(value, max),
    maxProgress: max,
  }));
}

const GamificationHub: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [stats, setStats] = useState<BadgeStats>({ challengesCompleted: 0, streak: 0, points: 0, level: 1, questsCompleted: 0 });
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);
  const [earnedBadgeNames, setEarnedBadgeNames] = useState<string[]>([]);

  const loadGamificationData = useCallback(async () => {
    try {
      const progressResponse = await API.get('/progress');
      if (progressResponse && progressResponse.length > 0) {
        const progress = progressResponse[0];
        const newStats: BadgeStats = {
          challengesCompleted: progress.challengesCompleted || 0,
          streak: progress.streak || 0,
          points: progress.points || 0,
          level: progress.level || 1,
          questsCompleted: progress.questsCompleted || 0,
        };
        setStats(newStats);
        setCompletedQuests(progress.completedQuests || []);
        setEarnedBadgeNames((progress.badges || []).map((b: any) => (typeof b === 'string' ? b : b.name)));
        setAchievements(generateAchievements(newStats));
      }
    } catch (error) {
      // Keep whatever was already loaded -- this page is a nicety, not a gate.
    }
  }, []);

  useEffect(() => {
    loadGamificationData();
    const handleChallengeCompleted = () => { setTimeout(loadGamificationData, 1000); };
    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    return () => window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
  }, [loadGamificationData]);

  const earnedBadges = computeBadges(stats, completedQuests, earnedBadgeNames).filter((b) => b.earned);
  const unlockedCount = achievements.filter((a) => a.unlocked).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div className="ws-fade-up" style={panel}>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Badges &amp; rewards</h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Every achievement and honor you&rsquo;ve earned, in one place.</p>
      </div>

      <div className="ws-fade-up ws-gamification-stats" style={{ display: 'flex', alignItems: 'stretch', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 8px', animationDelay: '60ms', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="points" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{stats.points}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Credits</span>
          </div>
        </div>
        <div style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 12, padding: '6px 18px' }}>
          <RankRing level={stats.level} progress={0} size={34} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{getRankName(stats.level)}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Level {stats.level}</span>
          </div>
        </div>
        <div style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="missions" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{stats.challengesCompleted}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Missions</span>
          </div>
        </div>
        <div style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />
        <div style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
          <LucideIcon name="badges" size={17} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{earnedBadges.length}</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Honors</span>
          </div>
        </div>
      </div>

      <div className="ws-gamification-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        <div className="ws-fade-up" style={{ ...panel, animationDelay: '100ms' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Achievements</h2>
            <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{unlockedCount}/{achievements.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 560, overflowY: 'auto' }}>
            {achievements.map((a) => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px',
                borderRadius: 10, border: `1.5px solid ${a.unlocked ? 'var(--ember)' : 'var(--border)'}`,
                background: a.unlocked ? 'var(--ember-soft)' : 'var(--surface-sunken)', opacity: a.unlocked ? 1 : 0.75,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ flex: '0 0 auto', color: a.unlocked ? 'var(--ember-strong)' : 'var(--text-tertiary)' }}><LucideIcon name={a.icon} size={20} /></span>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{a.name}</p>
                    <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-secondary)' }}>{a.description}</p>
                  </div>
                </div>
                {a.unlocked ? (
                  <span style={{ color: 'var(--success)', flex: '0 0 auto' }}><LucideIcon name="status-check" size={18} /></span>
                ) : (
                  <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
                    <span className="ws-mono" style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{a.progress}/{a.maxProgress}</span>
                    <div style={{ width: 60, height: 5, borderRadius: 999, background: 'var(--border)', marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${(a.progress / a.maxProgress) * 100}%`, height: '100%', borderRadius: 999, background: 'var(--ember)' }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="ws-fade-up" style={{ ...panel, animationDelay: '140ms' }}>
          <h2 className="ws-display" style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Honor collection</h2>
          {earnedBadges.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--text-tertiary)' }}><LucideIcon name="Award" size={36} /></div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Complete a lesson to earn your first badge.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
              {earnedBadges.map((badge) => {
                const style = RARITY_STYLE[badge.rarity];
                return (
                  <div key={badge.id} style={{ textAlign: 'center', padding: '14px 10px', borderRadius: 12, border: `1.5px solid ${style.border}`, background: style.bg }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: style.fg }}><LucideIcon name={badge.icon} size={24} /></div>
                    <p style={{ margin: '0 0 3px', fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{badge.name}</p>
                    <p style={{ margin: '0 0 6px', fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.35 }}>{badge.description}</p>
                    <span className="ws-mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: style.fg }}>{badge.rarity}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamificationHub;
